import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { RemoteImage } from '../../../shared/RemoteImage';
import { RecipePlaceholder } from '../../../shared/RecipePlaceholder';
import { resolveRecipeTitle } from '../../../../utils/recipeTitle';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';

/**
 * The dish hovered in the Library drawer, drawn faded in the slot it would
 * fill. The slot's row is always the open one while the drawer fills it
 * (rowFocus.ts `effectiveFocusRow`), and the card fills the cell like
 * `SlotCard` and `EmptySlot` do, so the row does not jump as the cursor moves
 * down the list. Not interactive: the drawer's scrim covers the grid while it
 * is open.
 */
export function SlotPreviewCard({ recipe }: { recipe: RecipeIndexEntry }) {
  const { i18n } = useTranslation();
  return (
    <View style={styles.card} testID="slot-preview">
      <View style={styles.photoWrap}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.photo}
          resizeMode="cover"
          fallback={<RecipePlaceholder recipe={recipe} size={88} style={styles.photo} />}
        />
      </View>
      <View style={styles.content}>
        <Text style={styles.title} numberOfLines={3}>{resolveRecipeTitle(recipe, i18n.language)}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minHeight: 192,
    borderRadius: 10,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: Paprika.primary,
    backgroundColor: Paprika.surfaceLowest,
    overflow: 'hidden',
  },
  photoWrap: {
    flex: 1,
    minHeight: 88,
    opacity: 0.6,
    backgroundColor: Paprika.surfaceLow,
  },
  photo: {
    width: '100%' as any,
    height: '100%' as any,
  },
  content: {
    padding: 8,
    minHeight: 64,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 13,
    lineHeight: 16,
    color: Paprika.primary,
  },
});
